import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, Shield, Dumbbell, TrendingUp, Camera, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const Subscribe = () => {
  const navigate = useNavigate();
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `/api/payments/create-checkout`,
        { origin_url: originUrl },
        { withCredentials: true }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setSubscribing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('auth_token');
    } catch (e) {}
    navigate('/login');
  };

  const features = [
    { icon: Dumbbell, text: '155 Progressive Workouts' },
    { icon: Camera, text: 'Exercise Demo Photos' },
    { icon: TrendingUp, text: '6-Level Progression System' },
    { icon: Trophy, text: 'Badges & Achievement Tracking' },
    { icon: Shield, text: 'Full Program Access' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter uppercase mb-2">
            Iron Dad
          </h1>
          <p className="text-primary font-bold uppercase tracking-wider text-sm">From Dad Bod to Beast</p>
        </div>

        {/* Pricing Card */}
        <div className="bg-card border-2 border-accent rounded-sm p-8 mb-6">
          <div className="text-center mb-6">
            <div className="inline-block bg-accent/20 border border-accent/50 text-accent text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
              VIP Pre-Sale - Limited Spots
            </div>
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-2xl font-bold text-muted-foreground line-through">$59</span>
              <span className="text-5xl font-extrabold text-accent">$39</span>
            </div>
            <p className="text-muted-foreground text-sm">AUD per month</p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-accent" />
                </div>
                <span className="text-sm font-medium">{feature.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Subscribe Button */}
          <Button
            data-testid="subscribe-button"
            onClick={handleSubscribe}
            disabled={subscribing}
            className="w-full bg-accent text-black hover:bg-accent/90 rounded-sm font-bold uppercase tracking-wider h-14 text-lg"
          >
            {subscribing ? 'Redirecting to checkout...' : 'Get VIP Access - $39 AUD/month'}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">
            Monthly subscription. Cancel anytime. Supports Apple Pay, Google Pay & Credit Card.
          </p>
        </div>

        {/* Logout link */}
        <div className="text-center">
          <button
            onClick={handleLogout}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="logout-link"
          >
            Sign out
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Subscribe;
